import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, CreditCard, FileText, Plug, SlidersHorizontal } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageLoader } from '@/components/ui/Spinner';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { queryKeys } from '@/lib/query-keys';
import { ROUTES } from '@/routes/paths';
import type { ChannelName } from '@/types/integrations';
import { ChannelCard } from './ChannelCard';
import { CHANNEL_SPECS, PROVIDER_SPECS, channelSpec } from './integrations-catalog';
import { MetaOAuthPagesDialog } from './MetaOAuthPagesDialog';
import { metaOAuthRedirectFrom, withoutMetaOAuthParams } from './meta-oauth-redirect';
import { ProviderKeyCard } from './ProviderKeyCard';
import { MetaAdsCard } from './MetaAdsCard';
import { WompiCard } from './WompiCard';
import { DropiCard } from './DropiCard';
import { useIntegrationsOverview } from './use-integrations';

/**
 * Paso 3 del plan (`docs/FRONTEND_PLAN.md` §4): Integraciones.
 *
 * Es la cuarta parte de "Ajustes" pero tiene su propia entrada en el sidebar, porque es lo
 * primero que hace falta para que el bot funcione de punta a punta: las API keys de IA y los
 * canales de mensajería. Todo es por negocio (multi-tenant): cada tenant conecta sus propias
 * credenciales y el backend las guarda cifradas.
 *
 * Tres bloques:
 *  1. __Inteligencia artificial__: DeepSeek (cerebro conversacional) y Gemini (solo visión).
 *     Las keys nunca se devuelven: se ve `configurada` + una máscara.
 *  2. __Canales__: WhatsApp, Messenger e Instagram (TikTok sigue en el backend pero no se ofrece aquí),
 *     con conectar/probar/desconectar y
 *     el onboarding con Facebook Login (OAuth) para Messenger e Instagram.
 *  3. __Relacionado__: enlaces a las otras pestañas de Ajustes que se usan junto a esto.
 *
 * OJO con la vuelta del OAuth: el callback del backend redirige a ESTA ruta con
 * `?oauth=connected|select|error` cuando `META_OAUTH_FRONTEND_REDIRECT` está configurado (hoy
 * está vacío en `backend/.env`, así que `start` responde 400 con un mensaje claro y el botón de
 * Facebook lo muestra). La pantalla sabe leer los tres casos y limpiar los parámetros al cerrar.
 */

export function IntegrationsPage() {
  const { tenantId, canEdit } = useActiveTenantAccess();
  const queryClient = useQueryClient();
  const overviewQuery = useIntegrationsOverview(tenantId);
  const [searchParams, setSearchParams] = useSearchParams();

  const redirect = metaOAuthRedirectFrom(searchParams);
  const [pendingTicket, setPendingTicket] = useState<string | null>(null);
  const [oauthNotice, setOauthNotice] = useState<string | null>(null);

  useEffect(() => {
    // El canal se conectó en el backend mientras el navegador estaba en Facebook: hay que
    // volver a leer el resumen para pintarlo como conectado.
    if (redirect.status === 'connected' && tenantId) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.integrations.overview(tenantId) });
    }

    // Varias páginas candidatas → el backend devolvió un ticket para elegir una.
    if (redirect.status === 'select' && redirect.ticket) setPendingTicket(redirect.ticket);
  }, [redirect.status, redirect.ticket, tenantId, queryClient]);

  /** Quita de la URL los parámetros del onboarding (para que el aviso no se repita al recargar). */
  function clearRedirectParams() {
    setPendingTicket(null);
    setSearchParams(withoutMetaOAuthParams(searchParams), { replace: true });
  }

  if (!tenantId) {
    return (
      <EmptyState
        icon={<Plug size={22} />}
        title="No hay un negocio activo"
        description="Elige un negocio en el selector de la izquierda para conectar sus credenciales."
      />
    );
  }

  if (overviewQuery.isPending) return <PageLoader label="Cargando las integraciones…" />;

  if (overviewQuery.isError) return <Alert>{getErrorMessage(overviewQuery.error)}</Alert>;

  const overview = overviewQuery.data;
  if (!overview) return <PageLoader label="Cargando las integraciones…" />;

  const summaryOf = (channel: ChannelName) =>
    overview.channels.find((item) => item.channel === channel) ?? null;

  const oauthChannelLabel = redirect.channel ? channelSpec(redirect.channel).label : 'El canal';

  return (
    <div className="space-y-8">
      {/* ---------------- Vuelta del onboarding con Facebook (OAuth) ---------------- */}
      {redirect.status === 'error' ? (
        <Alert tone="danger">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{redirect.message ?? 'No se pudo completar la conexión con Facebook.'}</span>
            <Button variant="ghost" size="sm" onClick={clearRedirectParams}>
              Cerrar
            </Button>
          </div>
        </Alert>
      ) : null}

      {redirect.status === 'connected' ? (
        <Alert tone="success">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>
              {oauthChannelLabel} quedó conectado con Facebook
              {redirect.pageName ? ` (página ${redirect.pageName})` : ''}.
            </span>
            <Button variant="ghost" size="sm" onClick={clearRedirectParams}>
              Cerrar
            </Button>
          </div>
        </Alert>
      ) : null}

      {redirect.status === 'select' ? (
        <Alert tone="info">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>
              Tu cuenta de Facebook administra varias páginas: elige con cuál se conecta{' '}
              {oauthChannelLabel.toLowerCase()}.
            </span>
            <Button variant="ghost" size="sm" onClick={clearRedirectParams}>
              Descartar
            </Button>
          </div>
        </Alert>
      ) : null}

      {oauthNotice ? (
        <Alert tone="success">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{oauthNotice}</span>
            <Button variant="ghost" size="sm" onClick={() => setOauthNotice(null)}>
              Cerrar
            </Button>
          </div>
        </Alert>
      ) : null}

      {!canEdit ? (
        <Alert tone="info">
          Tu rol en este negocio es <strong className="font-semibold">Agente humano</strong>: puedes
          ver el estado de las integraciones, pero solo un administrador puede conectarlas o
          cambiar las keys.
        </Alert>
      ) : null}

      {/* --------------------------- Inteligencia artificial -------------------------- */}
      <section className="space-y-4">
        <div>
          <h2 className="text-card-title font-semibold text-ink">Inteligencia artificial</h2>
          <p className="mt-1 text-caption text-ink-muted">
            Cada negocio usa sus propias keys, guardadas cifradas y asociadas solo a este negocio.
            DeepSeek es el cerebro del bot; Gemini solo mira las fotos que mandan los clientes.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {PROVIDER_SPECS.map((spec) => (
            <ProviderKeyCard
              key={spec.provider}
              spec={spec}
              status={overview[spec.provider]}
              canEdit={canEdit}
            />
          ))}
        </div>
      </section>

      {/* -------------------------------- Meta Ads --------------------------------- */}
      <section className="space-y-4">
        <div>
          <h2 className="text-card-title font-semibold text-ink">Meta Ads</h2>
          <p className="mt-1 text-caption text-ink-muted">
            La atribución (de qué anuncio vino cada chat) ya llega sola por el webhook; con la cuenta
            publicitaria y un token de System User con `ads_read` se trae además el gasto y el nombre de
            la campaña, y el CPA de los reportes se calcula sin cargarlo a mano.
          </p>
        </div>

        <MetaAdsCard />
      </section>

      {/* ---------------------------- Canales de mensajería --------------------------- */}
      <section className="space-y-4">
        <div>
          <h2 className="text-card-title font-semibold text-ink">Canales de mensajería</h2>
          <p className="mt-1 text-caption text-ink-muted">
            Activa solo los canales que uses. WhatsApp necesita además `waba_id` y `access_token`
            para las plantillas; Messenger e Instagram se pueden conectar con Facebook Login.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {CHANNEL_SPECS.map((spec) => (
            <ChannelCard
              key={spec.channel}
              spec={spec}
              summary={summaryOf(spec.channel)}
              canEdit={canEdit}
            />
          ))}
        </div>
      </section>

      {/* --------------------------- Pago en línea (Wompi, opcional) ------------------ */}
      <section className="space-y-4">
        <div>
          <h2 className="text-card-title font-semibold text-ink">Pagos en línea</h2>
          <p className="mt-1 text-caption text-ink-muted">
            Opcional. Con Wompi el cliente puede pagar con tarjeta, Nequi o PSE desde el mismo chat.
            Tus <strong>métodos de pago de siempre</strong> (Ajustes → Métodos de pago) siguen
            funcionando igual: esto es una opción más.
          </p>
        </div>

        <WompiCard />
      </section>

      {/* ------------------- Dropshipping / fulfillment (Dropi, opcional) ----------- */}
      <section className="space-y-4">
        <div>
          <h2 className="text-card-title font-semibold text-ink">Dropshipping</h2>
          <p className="mt-1 text-caption text-ink-muted">
            Opcional. Con Dropi conectado, la venta que cierra el bot se puede enviar a tu cuenta de
            Dropi (queda pendiente de confirmación) y el panel te muestra la <strong>huella
            digital</strong> del cliente: cuántas órdenes ha recibido y cuántas ha devuelto.
          </p>
        </div>

        <DropiCard />
      </section>

      {/* ------------------------------- Relacionado -------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Ajustes que van de la mano con esto</CardTitle>
          <CardDescription>
            Las credenciales son la parte técnica; lo que el bot dice (y cobra) se configura en
            Ajustes. WhatsApp conectado con `waba_id` + `access_token` es requisito para las
            plantillas.
          </CardDescription>
        </CardHeader>

        <CardContent className="grid gap-3 sm:grid-cols-3">
          <RelatedLink
            to={ROUTES.settingsGeneral}
            icon={<SlidersHorizontal size={16} />}
            title="Asistentes y reglas"
            description="Nombres, tono, políticas, número del admin y reglas obligatorias."
          />
          <RelatedLink
            to={ROUTES.settingsPayments}
            icon={<CreditCard size={16} />}
            title="Métodos de pago"
            description="El bot ofrece solo los métodos activos, con sus datos y condiciones exactos."
          />
          <RelatedLink
            to={ROUTES.templates}
            icon={<FileText size={16} />}
            title="Plantillas de WhatsApp"
            description="Avisos fuera de la ventana de 24 h: necesitan el canal de WhatsApp conectado."
          />
        </CardContent>
      </Card>

      <MetaOAuthPagesDialog
        open={pendingTicket !== null}
        ticket={pendingTicket}
        channel={redirect.channel}
        onClose={clearRedirectParams}
        onConnected={(pageName) =>
          setOauthNotice(`Canal conectado con Facebook${pageName ? ` (página ${pageName})` : ''}.`)
        }
      />
    </div>
  );
}

/** Enlace interno de la sección "Relacionado" (mismo lenguaje que un ítem de navegación). */
function RelatedLink({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-start gap-3 rounded-panel border border-line bg-surface p-4 transition-colors hover:border-brand-200 hover:bg-brand-50/60"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-control bg-brand-50 text-brand-600">
        {icon}
      </span>

      <span className="min-w-0">
        <span className="block text-body font-medium text-ink">{title}</span>
        <span className="mt-0.5 block text-caption text-ink-muted">{description}</span>
      </span>

      <ChevronRight size={16} className="mt-1 ml-auto shrink-0 text-ink-faint" />
    </Link>
  );
}

